import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import type { User } from '../types';
import { api } from '../lib/api';

// Canal pra sincronizar logout entre tabs/janelas. Sem isso, fechar
// sessão numa aba não invalidava as outras — o access token vivia em
// memória da segunda aba até o próximo 401 (até 15min de "sessão fantasma"
// em estação compartilhada).
const AUTH_CHANNEL = 'whatsapp-tracker-auth';
const LOGOUT_EVENT = 'logout';
const LOGIN_EVENT = 'login';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  isSuperadmin: boolean;
}

export const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  login: async () => {},
  logout: async () => {},
  isSuperadmin: false,
});

export function useAuth() {
  return useContext(AuthContext);
}

export function useAuthProvider() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const bcRef = useRef<BroadcastChannel | null>(null);

  // Boot: tenta refresh pelo cookie httpOnly. Se existir sessão válida,
  // recebemos um accessToken novo em memória; caso contrário, 401 → login.
  const checkAuth = useCallback(async () => {
    try {
      const refreshed = await api.tryRefresh();
      if (!refreshed) {
        setLoading(false);
        return;
      }
      const userData = await api.getMe();
      setUser(userData);
    } catch {
      api.clearTokens();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // checkAuth eventualmente faz setUser/setLoading async — fire-and-forget
    // intencional. void marca o promise como ignorado pro typescript-eslint.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void checkAuth();
  }, [checkAuth]);

  // Quando api.ts detecta sessão expirada (refresh falhou em request 401),
  // chama este callback. Em vez de hard reload via window.location.href,
  // só limpamos o user state — RequireAuth/AppLayout então renderiza
  // <Navigate to="/login"> via React Router, preservando cache e Sentry.
  useEffect(() => {
    api.setOnAuthExpired(() => setUser(null));
    return () => api.setOnAuthExpired(null);
  }, []);

  // Sincroniza logout/login entre tabs via BroadcastChannel.
  // Fallback silencioso em browsers sem suporte (Safari < 15.4).
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const bc = new BroadcastChannel(AUTH_CHANNEL);
    bcRef.current = bc;
    bc.onmessage = (e) => {
      if (e.data === LOGOUT_EVENT) {
        api.clearTokens();
        setUser(null);
      } else if (e.data === LOGIN_EVENT) {
        // Outra tab fez login — re-checa estado pra puxar user atual
        // (cookie refresh foi setado naquela tab; nossa tem que descobrir).
        void checkAuth();
      }
    };
    return () => {
      bc.close();
      bcRef.current = null;
    };
  }, [checkAuth]);

  const login = async (email: string, password: string) => {
    const data = await api.login(email, password);
    // api.login já seta o accessToken em memória; cookie de refresh vem do backend.
    setUser(data.user);
    try { bcRef.current?.postMessage(LOGIN_EVENT); } catch { /* aba isolada: BroadcastChannel indisponível não impede o login */ }
  };

  const logout = async () => {
    try {
      await api.logout();
    } catch {
      // ignora erro no logout — limpar estado é o que importa
    }
    api.clearTokens();
    setUser(null);
    try { bcRef.current?.postMessage(LOGOUT_EVENT); } catch { /* idem: o estado local já foi limpo */ }
  };

  return {
    user,
    loading,
    login,
    logout,
    isSuperadmin: user?.role === 'superadmin',
  };
}
